import { ulid } from "ulid";
import { useEffect, useRef } from "react";
import { useAuth } from "context/useAuth";
import { Firestore, collection, doc, getDoc, setDoc, deleteDoc, getDocs, query, QueryConstraint, DocumentData, onSnapshot, limit } from "firebase/firestore";

export class FirestoreCollection<T> {
    private db: Firestore;
    public collectionName: string;
    private unsubscribeCallbacks: (() => void)[] = [];

    constructor(db: Firestore, collectionName: string) {
        this.db = db;
        this.collectionName = collectionName;
    }

    async getOne(id: string): Promise<T> {
        try {
            const ref = doc(this.db, this.collectionName, id);
            const docSnap = await getDoc(ref);
            if (!docSnap.exists()) return {} as T;
            return docSnap.data() as T;
        } catch (error) {
            console.error("Error in getOne: ", error);
            return {} as T;
        }
    }

    async setConfig(data: any, getConfig?: () => Promise<any>): Promise<any> {
        try {
            if (getConfig) {
                const config = await getConfig();
                if (config) data.ConfigurationStatusLog.configuration = config;
            }
        } catch (e) {}
        return data;
    }

    getOneStream(id: string, callback: (data: T | null) => void, getConfig?: () => Promise<any>): () => void {
        try {
            const ref = doc(this.db, this.collectionName, id);

            // immediately
            
            this.getOne(id).then(async data => {
                if (data) callback(data as T);
            }).catch(err => {
                console.error("Initial fetch failed in getOneStream:", err);
            });

            // repeated fetch
            
            const intervalId = setInterval(() => {
                this.getOne(id).then(async data => {
                    if (data) callback(await this.setConfig(data, getConfig) as T);
                }).catch(err => {
                    console.error("Repeated fetch failed in getOneStream:", err);
                });
            }, 5000);

            // real-time listener
            
            const unsubscribe = onSnapshot(ref, async docSnap => {
                if (docSnap.exists()) callback(docSnap.data() as T);
            });
            
            this.unsubscribeCallbacks.push(unsubscribe);
            this.unsubscribeCallbacks.push(() => clearInterval(intervalId));

            return () => {
                unsubscribe();
                clearInterval(intervalId)
                this.unsubscribeCallbacks = this.unsubscribeCallbacks.filter(unsub => unsub !== unsubscribe);
            };

        } catch (error) {
            console.error("Error in getOneStream:", error);
            return () => null;
        }
    }

    async queryOne(queryConstraints?: QueryConstraint[]): Promise<{ id: string; data: T }> {
        const q = query(collection(this.db, this.collectionName), ...(queryConstraints ?? []), limit(1));
        const snap = await getDocs(q);
        if (snap.empty) return undefined as any;
        return { id: snap.docs[0].id, data: snap.docs[0].data() as T };
    }

    async queryMany(queryConstraints?: QueryConstraint[]): Promise<{ id: string; data: T }[]> {
        const q = query(collection(this.db, this.collectionName), ...(queryConstraints ?? []));
        return (await getDocs(q)).docs.map(doc => ({ id: doc.id, data: doc.data() as T }));
    }

    queryManyStream(queryConstraints: QueryConstraint[], callback: (data: { id: string; data: T }[]) => void): () => void {
        const q = query(collection(this.db, this.collectionName), ...queryConstraints);
        const unsubscribe = onSnapshot(q, (snapshot) => {
            callback(snapshot.docs.map(doc => ({ id: doc.id, data: doc.data() as T })));
        });
        this.unsubscribeCallbacks.push(unsubscribe);
        return () => {
            unsubscribe();
            this.unsubscribeCallbacks = this.unsubscribeCallbacks.filter(unsub => unsub !== unsubscribe);
        };
    }

    async insertOne(data: T): Promise<string> {
        const id = ulid();
        const ref = doc(this.db, this.collectionName, id);
        await setDoc(ref, data as DocumentData, { merge: true });
        return id;
    }

    async insertOrUpdate(id: string, data: T): Promise<string> {
        const ref = doc(this.db, this.collectionName, id);
        await setDoc(ref, data as DocumentData, { merge: true });
        return id;
    }

    async updateOne(id: string, data: Partial<T>): Promise<void> {
        const ref = doc(this.db, this.collectionName, id);
        await setDoc(ref, data as DocumentData, { merge: true });
    }

    async deleteOne(id: string): Promise<void> {
        const ref = doc(this.db, this.collectionName, id);
        await deleteDoc(ref);
    }

    unsubscribeAll(): void {
        this.unsubscribeCallbacks.forEach(unsub => unsub());
        this.unsubscribeCallbacks = [];
    }
}

export function useCollection<T>(collectionName: string) {
    
    const { firestore } = useAuth();
    const collectionRef = useRef<FirestoreCollection<T> | null>(null);

    if (!collectionRef.current || collectionRef.current.collectionName !== collectionName) {
        collectionRef.current = new FirestoreCollection<T>(firestore!, collectionName);
    }

    useEffect(() => {
        const collectionInstance = collectionRef.current;
        return () => {
            collectionInstance?.unsubscribeAll();
        };
    }, [collectionName]);

    return collectionRef.current!;
}
